import React, { useState } from 'react';
import { ArrowUpRight, Play, Mic2, Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { articles, videos, moderatingSessions, services, personalInfo, images } from '../data/mockData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.status === 'success') {
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 10000);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again or email directly at jerry@fisayobambi.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1419] pt-20">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-4">
              Portfolio
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-[#f5f5f0] mb-6">
              My Work
            </h1>
            <p className="text-xl text-[#a0a0a0] max-w-2xl mx-auto">
              Explore my journalism, video content, and event moderation work across global platforms.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs Section */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Tabs defaultValue="articles" className="w-full">
            <TabsList className="w-full md:w-auto flex flex-wrap justify-center gap-2 bg-transparent p-0 mb-12">
              <TabsTrigger 
                value="articles" 
                className="px-6 py-3 rounded-full bg-[#1a1f2e] text-[#a0a0a0] border border-[#2a3441] data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0f1419] data-[state=active]:border-[#d4af37] transition-all"
              >
                Articles
              </TabsTrigger>
              <TabsTrigger 
                value="videos" 
                className="px-6 py-3 rounded-full bg-[#1a1f2e] text-[#a0a0a0] border border-[#2a3441] data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0f1419] data-[state=active]:border-[#d4af37] transition-all"
              >
                Videos
              </TabsTrigger>
              <TabsTrigger 
                value="moderating" 
                className="px-6 py-3 rounded-full bg-[#1a1f2e] text-[#a0a0a0] border border-[#2a3441] data-[state=active]:bg-[#d4af37] data-[state=active]:text-[#0f1419] data-[state=active]:border-[#d4af37] transition-all"
              >
                Moderating
              </TabsTrigger>
            </TabsList>

            {/* Articles Tab */}
            <TabsContent value="articles">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.map((article) => (
                  <a
                    key={article.id}
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <div className="bg-[#1a1f2e] rounded-xl overflow-hidden border border-[#2a3441] hover:border-[#d4af37]/50 transition-all duration-300 hover:-translate-y-1">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                            article.category === 'Politics' || 
                            article.title.includes('Botswana') || 
                            article.title.includes('Self-Esteem') ||
                            article.title.includes('Skin Care')
                              ? 'object-top' 
                              : 'object-center'
                          }`}
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-[#d4af37] text-[#0f1419] text-xs font-semibold rounded-full">
                            {article.category}
                          </span>
                        </div>
                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-10 h-10 rounded-full bg-[#d4af37] flex items-center justify-center">
                            <ArrowUpRight className="text-[#0f1419]" size={18} />
                          </div>
                        </div>
                      </div>
                      <div className="p-6">
                        <p className="text-[#a0a0a0] text-sm mb-2">
                          {new Date(article.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                        <h3 className="font-serif text-lg font-semibold text-[#f5f5f0] group-hover:text-[#d4af37] transition-colors mb-3">
                          {article.title}
                        </h3>
                        <p className="text-[#a0a0a0] text-sm line-clamp-2">
                          {article.excerpt}
                        </p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
              
              <div className="mt-12 text-center">
                <a
                  href="https://www.euronews.com/profiles/957"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-transparent hover:bg-[#d4af37]/10 border border-[#d4af37] text-[#d4af37] font-semibold px-8 py-6 rounded-full">
                    View More of Jerry's Work
                    <ArrowUpRight className="ml-2" size={18} />
                  </Button>
                </a>
              </div>
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {videos.map((video) => (
                  <a
                    key={video.id}
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block"
                  >
                    <div className="bg-[#1a1f2e] rounded-xl overflow-hidden border border-[#2a3441] hover:border-[#d4af37]/50 transition-all duration-300 hover:-translate-y-1">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-[#0f1419]/40 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-[#d4af37] flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                            <Play className="text-[#0f1419] ml-1" size={24} fill="currentColor" />
                          </div>
                        </div>
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 bg-[#1a1f2e]/80 text-[#f5f5f0] text-xs font-medium rounded-full">
                            {video.platform}
                          </span>
                        </div>
                      </div>
                      <div className="p-6">
                        <h3 className="font-serif text-lg font-semibold text-[#f5f5f0] group-hover:text-[#d4af37] transition-colors mb-3">
                          {video.title}
                        </h3>
                        <p className="text-[#a0a0a0] text-sm">
                          {video.description}
                        </p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
              
              <div className="mt-12 text-center">
                <a
                  href="https://www.youtube.com/@fisayobambi"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="bg-transparent hover:bg-[#d4af37]/10 border border-[#d4af37] text-[#d4af37] font-semibold px-8 py-6 rounded-full">
                    Subscribe on YouTube
                    <ArrowUpRight className="ml-2" size={18} />
                  </Button>
                </a>
              </div>
            </TabsContent>

            {/* Moderating Tab */}
            <TabsContent value="moderating">
              <div className="space-y-6">
                {/* Featured Moderating Image */}
                <div className="relative rounded-2xl overflow-hidden mb-12">
                  <img
                    src={images.moderating}
                    alt="Jerry Fisayo-Bambi moderating at Euronews Tech Summit"
                    className="w-full h-[400px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419] via-[#0f1419]/50 to-transparent" />
                  <div className="absolute bottom-8 left-8 right-8">
                    <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-2">
                      Event Moderation
                    </p>
                    <h3 className="font-serif text-3xl font-bold text-[#f5f5f0]">
                      Facilitating Conversations That Matter
                    </h3>
                  </div>
                </div>

                {/* Moderating Sessions List */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {moderatingSessions.map((session) => (
                    <div
                      key={session.id}
                      className="bg-[#1a1f2e] rounded-xl p-6 border border-[#2a3441] hover:border-[#d4af37]/50 transition-all duration-300"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 flex items-center justify-center flex-shrink-0">
                          <Mic2 className="text-[#d4af37]" size={24} />
                        </div>
                        <div className="space-y-2">
                          <span className="px-3 py-1 bg-[#d4af37]/10 text-[#d4af37] text-xs font-medium rounded-full">
                            {session.date}
                          </span>
                          <h3 className="font-serif text-lg font-semibold text-[#f5f5f0]">
                            {session.title}
                          </h3>
                          <p className="text-[#d4af37] text-sm font-medium">
                            {session.event}
                          </p>
                          <p className="text-[#a0a0a0] text-sm leading-relaxed">
                            {session.description}
                          </p>
                          <p className="text-[#6b7280] text-xs flex items-center">
                            <MapPin size={12} className="mr-1" /> {session.location}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-[#0a0d10]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-2">
              Services
            </p>
            <h2 className="font-serif text-4xl font-bold text-[#f5f5f0] mb-4">
              How I Can Help
            </h2>
            <p className="text-[#a0a0a0] max-w-2xl mx-auto">
              From media campaigns to communication strategy, I help organizations tell their stories effectively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => (
              <div
                key={service.id}
                className="group p-6 bg-[#1a1f2e] rounded-xl border border-[#2a3441] hover:border-[#d4af37]/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-xl bg-[#d4af37]/10 flex items-center justify-center text-[#d4af37] mb-4 group-hover:bg-[#d4af37] group-hover:text-[#0f1419] transition-all duration-300">
                  {service.icon === 'megaphone' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m3 11 18-5v12L3 13v-2z"/>
                      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/>
                    </svg>
                  )}
                  {service.icon === 'strategy' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                    </svg>
                  )}
                  {service.icon === 'microphone' && <Mic2 size={24} />}
                  {service.icon === 'training' && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                      <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                    </svg>
                  )}
                </div>
                <h3 className="font-serif text-lg font-semibold text-[#f5f5f0] mb-2">
                  {service.title}
                </h3>
                <p className="text-[#a0a0a0] text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <p className="text-[#d4af37] font-medium tracking-widest uppercase text-sm mb-4">
                  Get In Touch
                </p>
                <h2 className="font-serif text-4xl font-bold text-[#f5f5f0] mb-6">
                  Let's Work Together
                </h2>
                <p className="text-[#a0a0a0] text-lg leading-relaxed">
                  Whether you're looking for a moderator for your next event, need media strategy 
                  consultation, or want to collaborate on a project, I'd love to hear from you.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 flex items-center justify-center">
                    <Mail className="text-[#d4af37]" size={20} />
                  </div>
                  <div>
                    <p className="text-[#6b7280] text-sm">Email</p>
                    <a href={`mailto:${personalInfo.email}`} className="text-[#f5f5f0] hover:text-[#d4af37] transition-colors">
                      {personalInfo.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/10 flex items-center justify-center">
                    <MapPin className="text-[#d4af37]" size={20} />
                  </div>
                  <div>
                    <p className="text-[#6b7280] text-sm">Based in</p>
                    <p className="text-[#f5f5f0]">{personalInfo.location}</p>
                  </div>
                </div>
              </div>

              {/* Featured Image */}
              <div className="relative rounded-xl overflow-hidden mt-8">
                <img
                  src={images.speaking}
                  alt="Jerry Fisayo-Bambi speaking"
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f1419]/80 to-transparent" />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[#1a1f2e] rounded-2xl p-8 border border-[#2a3441]">
              <h3 className="font-serif text-2xl font-bold text-[#f5f5f0] mb-6">
                Send a Message
              </h3>

              {submitted ? (
                <div className="bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-xl p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#d4af37]/20 flex items-center justify-center mx-auto mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <h4 className="font-serif text-xl font-semibold text-[#f5f5f0] mb-2">
                    Message Sent!
                  </h4>
                  <p className="text-[#a0a0a0]">
                    Thank you for reaching out. Jerry will get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                      <p className="text-red-400 text-sm">{error}</p>
                    </div>
                  )}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#a0a0a0] text-sm mb-2">Your Name</label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Doe"
                        className="bg-[#0f1419] border-[#2a3441] text-[#f5f5f0] placeholder:text-[#6b7280] focus:border-[#d4af37] focus:ring-[#d4af37]/20"
                      />
                    </div>
                    <div>
                      <label className="block text-[#a0a0a0] text-sm mb-2">Your Email</label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="john@example.com"
                        className="bg-[#0f1419] border-[#2a3441] text-[#f5f5f0] placeholder:text-[#6b7280] focus:border-[#d4af37] focus:ring-[#d4af37]/20"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#a0a0a0] text-sm mb-2">Subject</label>
                    <Input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder="Event Moderation Inquiry"
                      className="bg-[#0f1419] border-[#2a3441] text-[#f5f5f0] placeholder:text-[#6b7280] focus:border-[#d4af37] focus:ring-[#d4af37]/20"
                    />
                  </div>

                  <div>
                    <label className="block text-[#a0a0a0] text-sm mb-2">Message</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Tell me about your project or inquiry..."
                      rows={5}
                      className="bg-[#0f1419] border-[#2a3441] text-[#f5f5f0] placeholder:text-[#6b7280] focus:border-[#d4af37] focus:ring-[#d4af37]/20 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#d4af37] hover:bg-[#b8962d] text-[#0f1419] font-semibold py-6 rounded-full transition-transform hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        Send Message
                        <Send className="ml-2" size={18} />
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkPage;